import { RolesTenantsMappings } from '@components/Authorization';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import { stringRequiredValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { isEmpty } from 'lodash';
import React from 'react';
import { IAccessKey, AccessKeyType } from 'types/accessKeys';
import { IDataResponse } from 'types/common';
import { object } from 'yup';
import PermittedProjects from './PermittedProjects';
import { IAccessKeyRowAction } from './types';

const AccessKeyEditDialog: React.FC<
	IAccessKeyRowAction<IDataResponse<IAccessKey>, IAccessKey> & {
		showTenantMapping?: boolean;
	}
> = ({ onComplete, row, mutation, accessKeyType, showTenantMapping }) => {
	const { invokeToastableAction } = useToast();

	const formik = useFormik<IAccessKey>({
		initialValues: row?.row || {},
		validationSchema: object({
			name: stringRequiredValidationScheme()
		}),
		onSubmit: async (values) => {
			await invokeToastableAction(
				() => mutation.mutateAsync(values),
				`${accessKeyType}.action.edit.dialog.toast`,
				onComplete
			);
		}
	});

	const isMappingEmpty = isEmpty(formik.values.mappings);

	return (
		<Dialog open maxWidth="sm">
			<DialogTitle>
				{intl.tr(`${accessKeyType}.action.edit.dialog.title`)}
			</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextFieldValidation
								id="id"
								formik={formik}
								disabled
								copy
								fullWidth
								autoFocus
								label={intl.tr('fields.accessKeys.column.id')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextFieldValidation
								id="name"
								formik={formik}
								required
								fullWidth
								autoFocus
								label={intl.tr('fields.accessKeys.column.name')}
							/>
						</Grid>
						{showTenantMapping && (
							<Grid item xs={12}>
								<Typography pl={1} py={1}>
									{intl.tr('dialog.authorization.header')}
								</Typography>
								{isMappingEmpty ? (
									<Typography pl={1} py={1} variant="body2">
										{intl.tr('dialog.authorization.mappings.empty')}
									</Typography>
								) : (
									<RolesTenantsMappings
										formik={formik}
										id="mappings"
										disabled
									/>
								)}
							</Grid>
						)}
						{accessKeyType === AccessKeyType.management && (
							<PermittedProjects formik={formik} enabled={false} />
						)}
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onComplete}
					data-testid="access-key-edit-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="access-key-edit-dialog-approve-button"
				>
					{intl.tr('button.save')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

AccessKeyEditDialog.defaultProps = {
	showTenantMapping: false
};

export default AccessKeyEditDialog;
